import '../global.css'

import React, { useCallback, useEffect, useReducer, useRef } from 'react'
// oxlint-disable-next-line react-doctor/rn-prefer-reanimated -- RN Animated is patched below to disable native driver on web.
import { Animated, Platform, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Constants from 'expo-constants'
import { useFonts } from 'expo-font'
import { useNavigationContainerRef } from 'expo-router'
import Head from 'expo-router/head'
import * as SplashScreen from 'expo-splash-screen'
import {
  Cairo_300Light,
  Cairo_400Regular,
  Cairo_500Medium,
  Cairo_600SemiBold,
  Cairo_700Bold,
} from '@expo-google-fonts/cairo'
import { Lora_400Regular, Lora_700Bold } from '@expo-google-fonts/lora'
import {
  OpenSans_400Regular,
  OpenSans_700Bold,
} from '@expo-google-fonts/open-sans'
import * as Sentry from '@sentry/react-native'
import { useIsRestoring } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { useTranslation } from 'react-i18next'
import { Uniwind } from 'uniwind'

import { FullScreenErrorWall } from '../src/components/ui/FullScreenErrorWall'
import { ScreenLoadingFallback } from '../src/components/ui/ScreenLoadingFallback'
import { SentryErrorFallback } from '../src/components/ui/SentryErrorFallback'
import { AuthProvider } from '../src/contexts/AuthContext'
import { ToastProvider } from '../src/contexts/ToastContext'
import { useAppLifecycleAnalytics } from '../src/hooks/useAppLifecycleAnalytics'
import { useAppUpdateNotification } from '../src/hooks/useAppUpdateNotification'
import { ensureI18nReady } from '../src/i18n'
import { RootNavigator } from '../src/navigation/RootNavigator'
import {
  queryClient,
  queryPersistOptions,
  useFocusManager,
} from '../src/queries/query-client'
import { colors } from '../src/theme'
import { captureException } from '../src/utils/sentry'

const sentryExtra = Constants.expoConfig?.extra?.sentry as
  | {
      dsn?: string | { ios?: string; android?: string; web?: string }
      environment?: string
      tracesSampleRate?: number
    }
  | undefined
const buildExtra = Constants.expoConfig?.extra?.build as
  | {
      flavour?: string
      version?: string
      buildNumber?: string
    }
  | undefined

// Config embeds per-platform DSN as an object; Sentry expects a string.
const sentryDsn =
  sentryExtra?.dsn == null
    ? undefined
    : typeof sentryExtra.dsn === 'string'
      ? sentryExtra.dsn
      : sentryExtra.dsn[Platform.OS as keyof typeof sentryExtra.dsn]

const isSentryEnabled = !__DEV__ && !!sentryDsn
const isAnalyticsEnabled = !__DEV__
let hasDisabledNativeDriverOnWeb = false

type StartupI18nStatus = 'loading' | 'ready' | 'error'

function disableNativeDriverOnWeb() {
  if (Platform.OS !== 'web' || hasDisabledNativeDriverOnWeb) {
    return
  }

  hasDisabledNativeDriverOnWeb = true

  const originalTiming = Animated.timing
  const patchedTiming: typeof Animated.timing = (value, config) =>
    originalTiming(value, { ...config, useNativeDriver: false })
  Object.defineProperty(Animated, 'timing', { value: patchedTiming })

  const originalSpring = Animated.spring
  const patchedSpring: typeof Animated.spring = (value, config) =>
    originalSpring(value, { ...config, useNativeDriver: false })
  Object.defineProperty(Animated, 'spring', { value: patchedSpring })

  const originalDecay = Animated.decay
  const patchedDecay: typeof Animated.decay = (value, config) =>
    originalDecay(value, { ...config, useNativeDriver: false })
  Object.defineProperty(Animated, 'decay', { value: patchedDecay })

  const originalEvent = Animated.event
  const patchedEvent: typeof Animated.event = (argMapping, config) =>
    originalEvent(argMapping, {
      ...(config ?? {}),
      useNativeDriver: false,
    })
  Object.defineProperty(Animated, 'event', { value: patchedEvent })
}

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: Platform.OS !== 'web',
})

function getReplayIntegration() {
  if (Platform.OS === 'web') {
    return Sentry.browserReplayIntegration()
  }

  return Sentry.mobileReplayIntegration()
}

const replayIntegration = getReplayIntegration()

Sentry.init({
  dsn: sentryDsn,
  environment: sentryExtra?.environment,
  release: `vocalise@${buildExtra?.version ?? '0.0.0-dev'}`,
  dist: buildExtra?.buildNumber ?? '0',
  enabled: isSentryEnabled,
  tracesSampleRate: sentryExtra?.tracesSampleRate,
  integrations: [navigationIntegration, replayIntegration],
  enableNativeFramesTracking: Platform.OS !== 'web',
  initialScope: {
    tags: {
      'build.flavour': buildExtra?.flavour ?? 'unknown',
      'app.version': buildExtra?.version ?? '0.0.0-dev',
    },
  },
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
})

void SplashScreen.preventAutoHideAsync().catch(() => {
  // Ignore duplicate-call races during fast refresh/startup retries.
})

disableNativeDriverOnWeb()

function UniwindInsetsSync() {
  const insets = useSafeAreaInsets()
  const previousInsetsKeyRef = useRef<string | null>(null)

  useEffect(() => {
    const insetsKey = `${insets.top}:${insets.right}:${insets.bottom}:${insets.left}`
    if (previousInsetsKeyRef.current === insetsKey) {
      return
    }

    previousInsetsKeyRef.current = insetsKey
    Uniwind.updateInsets(insets)
  }, [insets])

  return null
}

function AppShell() {
  const { t, i18n } = useTranslation(['common'])

  useAppLifecycleAnalytics({
    enabled: isAnalyticsEnabled,
    appVersion: buildExtra?.version ?? 'unknown',
    buildNumber: buildExtra?.buildNumber ?? 'unknown',
    locale: i18n.language,
    flavour: buildExtra?.flavour ?? 'unknown',
    environment: sentryExtra?.environment ?? 'production',
  })

  useAppUpdateNotification()

  return (
    <AuthProvider>
      <Head>
        <title>{t('common:page_title_app')}</title>
        <meta name="theme-color" content={colors.backgroundPrimary} />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      </Head>
      <UniwindInsetsSync />
      <View className="flex-1 w-full h-full web:h-dvh web:overflow-hidden bg-background-primary web:max-w-[480px] web:self-center">
        <ToastProvider>
          <RootNavigator />
        </ToastProvider>
      </View>
    </AuthProvider>
  )
}

function StartupTranslationErrorFallback({ onRetry }: { onRetry: () => void }) {
  return (
    <FullScreenErrorWall
      title="We could not load the app"
      message="Check your internet connection and try again."
      primaryActionLabel="Try again"
      onPrimaryAction={onRetry}
    />
  )
}

function RootLayoutBootstrap() {
  const ref = useNavigationContainerRef()
  const isRestoringQueryCache = useIsRestoring()
  const i18nStatusRef = useRef<StartupI18nStatus>('loading')
  const [i18nViewStatus, setI18nViewStatus] = useReducer(
    (_currentStatus: StartupI18nStatus, nextStatus: StartupI18nStatus) =>
      nextStatus,
    'loading',
  )
  const [fontsLoaded, fontsError] = useFonts({
    Cairo_300Light,
    Cairo_400Regular,
    Cairo_500Medium,
    Cairo_600SemiBold,
    Cairo_700Bold,
    Lora_400Regular,
    Lora_700Bold,
    OpenSans_400Regular,
    OpenSans_700Bold,
  })
  const setI18nStatus = useCallback((status: StartupI18nStatus) => {
    if (i18nStatusRef.current === status) {
      return
    }

    i18nStatusRef.current = status
    setI18nViewStatus(status)
  }, [])

  useEffect(() => {
    if (ref?.current) {
      navigationIntegration.registerNavigationContainer(ref)
    }
  }, [ref])

  useEffect(() => {
    if (isRestoringQueryCache || i18nStatusRef.current !== 'loading') {
      return
    }

    let isCancelled = false

    void ensureI18nReady().then(
      () => {
        if (!isCancelled) {
          setI18nStatus('ready')
        }
      },
      (error) => {
        console.error('Failed to initialize i18n', error)
        captureException(error, {
          feature: 'i18n',
          tags: {
            flow: 'startup',
            phase: 'initialize',
            platform: Platform.OS,
          },
        })
        if (!isCancelled) {
          setI18nStatus('error')
        }
      },
    )

    return () => {
      isCancelled = true
    }
  }, [i18nViewStatus, isRestoringQueryCache, setI18nStatus])

  useEffect(() => {
    if (
      Platform.OS === 'web' ||
      isRestoringQueryCache ||
      i18nViewStatus === 'loading' ||
      (i18nViewStatus === 'ready' && !fontsLoaded && !fontsError)
    ) {
      return
    }

    void SplashScreen.hideAsync().catch(() => {
      // Ignore hide races if the splash is already dismissed.
    })
  }, [fontsError, fontsLoaded, i18nViewStatus, isRestoringQueryCache])

  useFocusManager()

  if (
    isRestoringQueryCache ||
    i18nViewStatus === 'loading' ||
    (i18nViewStatus === 'ready' &&
      Platform.OS === 'web' &&
      !fontsLoaded &&
      !fontsError)
  ) {
    return <ScreenLoadingFallback />
  }

  if (i18nViewStatus === 'error') {
    return (
      <StartupTranslationErrorFallback
        onRetry={() => setI18nStatus('loading')}
      />
    )
  }

  return <AppShell />
}

function RootLayout() {
  return (
    <Sentry.ErrorBoundary
      fallback={(props) => (
        <SentryErrorFallback {...props} showReportIssueAction={false} />
      )}
    >
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={queryPersistOptions}
      >
        <RootLayoutBootstrap />
      </PersistQueryClientProvider>
    </Sentry.ErrorBoundary>
  )
}

export default Sentry.wrap(RootLayout)
